import type { PerformanceTrendEntry } from '../../types/agent-studio';

// Stroke colors reference CSS custom properties from decision-brief.css.
// var(--navy) for latency, var(--orange) for cost, var(--red) for error_rate.
const METRIC_STROKE: Record<PerformanceSparklineProps['metric'], string> = {
  avg_latency: 'var(--navy)',
  cost:        'var(--orange)',
  error_rate:  'var(--red)',
};

const METRIC_LABELS: Record<PerformanceSparklineProps['metric'], string> = {
  avg_latency: 'Задержка',
  cost:        'Стоимость',
  error_rate:  'Ошибки',
};

export interface PerformanceSparklineProps {
  data: PerformanceTrendEntry[];
  metric: 'avg_latency' | 'cost' | 'error_rate';
  width?: number;
  height?: number;
  label?: string;
}

function formatLastValue(metric: PerformanceSparklineProps['metric'], value: number): string {
  if (metric === 'avg_latency') return `${value.toFixed(1)}с`;
  if (metric === 'cost') return `$${value.toFixed(4)}`;
  return `${(value * 100).toFixed(1)}%`;
}

export function PerformanceSparkline({
  data,
  metric,
  width = 120,
  height = 32,
  label,
}: PerformanceSparklineProps) {
  const PAD = 4;
  const innerW = width - PAD * 2;
  const innerH = height - PAD * 2;
  const strokeColor = METRIC_STROKE[metric];
  const displayLabel = label ?? METRIC_LABELS[metric];

  // Empty state
  if (data.length === 0) {
    return (
      <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <svg width={width} height={height} aria-hidden="true">
          <line
            x1={PAD} y1={height / 2} x2={width - PAD} y2={height / 2}
            style={{ stroke: 'var(--ink-tertiary)', strokeDasharray: '3 3', strokeWidth: 1 }}
          />
        </svg>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-tertiary)' }}>—</span>
        {displayLabel && (
          <span className="t-micro" style={{ fontSize: 9 }}>{displayLabel}</span>
        )}
      </div>
    );
  }

  const values = data.map(d => d[metric] as number);
  const lastValue = values[values.length - 1];

  // Single point — render a dot
  if (values.length === 1) {
    return (
      <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <svg width={width} height={height} aria-hidden="true">
          <circle
            cx={width / 2} cy={height / 2} r={3}
            style={{ fill: strokeColor }}
          />
        </svg>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-secondary)' }}>
          {formatLastValue(metric, lastValue)}
        </span>
        {displayLabel && (
          <span className="t-micro" style={{ fontSize: 9 }}>{displayLabel}</span>
        )}
      </div>
    );
  }

  // Multi-point sparkline
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1; // avoid division by zero when all values equal

  const points = values.map((v, i) => {
    const x = PAD + (i / (values.length - 1)) * innerW;
    const y = PAD + (1 - (v - min) / range) * innerH;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <svg width={width} height={height} aria-hidden="true" style={{ overflow: 'visible' }}>
        <polyline
          points={points.join(' ')}
          style={{ fill: 'none', stroke: strokeColor, strokeWidth: 1.5, strokeLinejoin: 'round' }}
        />
      </svg>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-secondary)' }}>
        {formatLastValue(metric, lastValue)}
      </span>
      {displayLabel && (
        <span className="t-micro" style={{ fontSize: 9 }}>{displayLabel}</span>
      )}
    </div>
  );
}
